import type { MouseEventHandler, ReactElement } from "react";
import { useMemo } from "react";

import { Link } from "react-router";

import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";

import { Image } from "../image/Image";
import { boxShadowFor } from "../utils/shadows";
import type { CardProps } from "./Card.types";
import { CardContent } from "./CardContent";
import type { CardLinkProps } from "./CardLink";

type StyleProps = {
  $borderTop: string;
  $bg: string;
  $shadow: string;
  $hoverShadow: string | null;
};

const INTERACTIVE_SELECTOR =
  "a, button, input, textarea, select, [role='button']";

const StyledCard = styled.div<StyleProps>`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  position: relative;
  flex: 1;
  background: ${({ $bg }) => $bg};
  width: 290px;
  border: none;
  border-top: ${({ $borderTop }) => $borderTop};
  box-shadow: ${({ $shadow }) => $shadow};
  border-radius: 0.3rem;
  min-height: fit-content;
  padding: 0;
  margin: 1rem 0;
  transition:
    box-shadow 0.15s ease,
    transform 0.15s ease;
  cursor: default;
  text-decoration: none;
  color: inherit;
  box-sizing: border-box;

  &:hover,
  &:focus,
  &:visited {
    color: inherit;
    text-decoration: none;
  }

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }

  &.is-fluid {
    width: 100%;
  }

  &.is-clickable {
    cursor: pointer;
  }

  &.is-clickable:not(.is-placeholder):hover {
    box-shadow: ${({ $hoverShadow }) => $hoverShadow};
    &.elevation-small {
      transform: scale(1.01);
    }
    &.elevation-medium {
      transform: scale(1.025);
    }
    &.elevation-large {
      transform: scale(1.04);
    }
  }

  &.is-placeholder {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    max-width: initial;
    animation: none;
    overflow: visible;
  }

  > img,
  > ${Image} {
    display: block;
    width: 100%;
    border-radius: 0.3rem 0.3rem 0 0;
  }

  > ${CardContent}:last-child {
    border-bottom-left-radius: 0.3rem;
    border-bottom-right-radius: 0.3rem;
  }
`;

export const RenderCard = (
  props: (CardProps | CardLinkProps) & { asLink?: boolean },
): ReactElement => {
  const {
    fluid,
    elevation: elevationProp,
    against,
    color,
    image,
    placeholder,
    children,
    className,
    style,
    onClick,
    asLink,
    ...rest
  } = props;
  const elevation = elevationProp ?? "medium";

  const theme = useTheme();

  const borderTop = useMemo(() => {
    return color ? `2px solid ${theme.colors[color].ink}` : "none";
  }, [theme, color]);

  const shadow = boxShadowFor({ elevation, against });

  const isClickable = !!onClick || asLink;
  let hoverShadow = null;
  if (isClickable) {
    const nextElevation = elevation === "small" ? "medium" : "large";
    hoverShadow = boxShadowFor({ elevation: nextElevation, against });
  }

  const handleClick: MouseEventHandler<HTMLElement> | undefined = onClick
    ? (e) => {
        const target = e.target;
        if (target instanceof Element) {
          const interactive = target.closest(INTERACTIVE_SELECTOR);
          if (interactive && interactive !== e.currentTarget) {
            return;
          }
        }
        onClick(e);
      }
    : undefined;

  return (
    <StyledCard
      as={asLink ? Link : undefined}
      className={cx(
        fluid && "is-fluid",
        isClickable && "is-clickable",
        placeholder && "is-placeholder",
        `elevation-${elevation}`,
        className,
      )}
      style={style}
      onClick={handleClick}
      $borderTop={borderTop}
      $bg={placeholder ? "#f9fafb" : "#ffffff"}
      $shadow={shadow}
      $hoverShadow={hoverShadow}
      {...(rest as object)}
    >
      {image}
      {children}
    </StyledCard>
  );
};

const CardComponent = (props: CardProps): ReactElement => (
  <RenderCard {...props} />
);

export const Card = styled(CardComponent)``;
